import isEqual from "lodash/isEqual";

import type { ISearchEngineDetails } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import { ESearchEngineDevice } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import type { ISearchEnginesKeywordsProps } from "app/components/React/Table/cells/SearchEnginesKeywordCell/SearchEnginesKeywordCellConst";
import { deviceEnumToNameMap } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import { allTrackers } from "app/services/track/track";

function searchEngineValue(searchEngine: ISearchEngineDetails): string {
    const { language, location, device } = searchEngine;
    const deviceValue = deviceEnumToNameMap[device ?? 0];
    const valueOfSearchEngine = [location?.text, language?.id, deviceValue].join("_");
    return valueOfSearchEngine;
}

export const getSearchEngineLocationValues = (value: ISearchEngineDetails[] = []): string[] =>
    value.map(searchEngineValue);

export const getSearchEngineAvailableLocations = (
    value: ISearchEngineDetails[] = [],
): ISearchEnginesKeywordsProps[] => {
    const searchEnginesItems: ISearchEnginesKeywordsProps[] = value.map((searchEngine) => {
        const { language, location, device } = searchEngine;
        const deviceValue = deviceEnumToNameMap[device ?? ESearchEngineDevice.COMBINED];
        const valueOfSearchEngine = searchEngineValue(searchEngine);
        const availableLocationsObject: ISearchEnginesKeywordsProps = {
            countryIconID: location?.id as number,
            id: valueOfSearchEngine,
            text: `${location?.text} (${language?.text})`,
            device: deviceValue,
            originalSearchEngineItem: searchEngine,
        };
        return availableLocationsObject;
    });

    return searchEnginesItems?.filter((seItem) => seItem.countryIconID !== undefined);
};

const CHECKBOX_ALL_SELECTED_TRACKING: Record<string, Partial<Record<"add" | "remove", [string, string]>>> = {
    importCSV: {
        add: ["Check box", "All search engines selected"],
        remove: ["Check box", "All search engines unselected"],
    },
    importKeywordsList: {
        add: ["Check box", "All search engine selected"],
        remove: ["Check box", "All search engines unselected"],
    },
    topBulkEdit: { add: ["Table", "add search engines"] },
    tableCell: {
        add: ["Check box", "All search engine selected"],
        remove: ["Check box", "All search engines unselected"],
    },
};

const onCheckboxAllSelectedTracking = (guidAddSection: string, isAllSelected: boolean) => {
    const action = isAllSelected ? "add" : "remove";
    const entry = CHECKBOX_ALL_SELECTED_TRACKING[guidAddSection]?.[action];
    if (!entry) {
        return;
    }
    const [category, name] = entry;
    allTrackers.dispatchTrackEvent(category, action, name);
};

export const selectAllSearchEngineDevices = (
    selectedLocations: string[],
    availableLocations,
    guidAddSection: string,
) => {
    const isAllSelected = selectedLocations.length === availableLocations.length;
    const locationsToUpdate = isAllSelected ? [] : [...availableLocations.map((location) => location.id)];

    onCheckboxAllSelectedTracking(guidAddSection, !isAllSelected);
    return locationsToUpdate;
};

const CHECKBOX_SINGLE_SELECTION_TRACKING: Record<string, Partial<Record<"add" | "remove", [string, string]>>> = {
    importCSV: {
        add: ["Check box", "Search engine checkbox selected"],
        remove: ["Check box", "Search engine checkbox unselected"],
    },
    importKeywordsList: {
        add: ["Check box", "Search engine checkbox selected"],
        remove: ["Check box", "Search engine checkbox unselected"],
    },
    topBulkEdit: {
        add: ["Check box", "Search engine checkbox selected"],
        remove: ["Check box", "Search engine checkbox unselected"],
    },
};

const onCheckboxSingleSelectionTracking = (guidForTracking: string, addCheckbox: boolean) => {
    const action = addCheckbox ? "add" : "remove";
    const entry = CHECKBOX_SINGLE_SELECTION_TRACKING[guidForTracking]?.[action];
    if (!entry) {
        return;
    }
    const [category, name] = entry;
    allTrackers.dispatchTrackEvent(category, action, name);
};

export const handleSelectedCheckboxLocation = (
    location: string,
    selectedLocations: string[],
    guidForTracking: string,
): string[] => {
    const filteredSelectedLocations = selectedLocations?.filter((locationID) => locationID !== location);

    const isLocationChanged: boolean = isEqual(filteredSelectedLocations, selectedLocations);
    const newLocations: string[] = isLocationChanged
        ? [...(selectedLocations ?? []), location]
        : filteredSelectedLocations;

    onCheckboxSingleSelectionTracking(guidForTracking, isLocationChanged);

    return newLocations;
};

export const updateAvailableSearchEngines = (
    availableSearchEngines: ISearchEnginesKeywordsProps[],
    selectedLocations: string[],
): ISearchEnginesKeywordsProps[] => {
    const searchEngineSelection = availableSearchEngines?.map((engine) => {
        return {
            ...engine,
            isSelected: selectedLocations.includes(engine.id),
        };
    });

    return searchEngineSelection;
};

export const getSearchEngineSelectedIds = (searchEnginesItems: ISearchEnginesKeywordsProps[]) => {
    return searchEnginesItems.filter((seItem) => seItem.isSelected).map((selectedSe) => selectedSe.id);
};
